import { sha256 } from "@noble/hashes/sha2.js";
import { utf8ToBytes } from "@noble/hashes/utils.js";
function rotateLeft(value, shift) {
    return ((value << shift) | (value >>> (32 - shift))) >>> 0;
}
function validateWords(words) {
    if (words.length !== 4 ||
        words.some((word) => !Number.isInteger(word) || word < 0 || word > 0xffffffff)) {
        throw new TypeError("xoshiro state must contain four uint32 words");
    }
    if (words.every((word) => word === 0))
        throw new TypeError("xoshiro state cannot be all zero");
}
export class Xoshiro128 {
    #words;
    #draws;
    constructor(state) {
        validateWords(state.words);
        if (state.draws < 0n)
            throw new TypeError("draw count cannot be negative");
        this.#words = [...state.words];
        this.#draws = state.draws;
    }
    nextUint32() {
        const [s0, s1, s2, s3] = this.#words;
        const result = Math.imul(rotateLeft(Math.imul(s1, 5) >>> 0, 7), 9) >>> 0;
        const shifted = (s1 << 9) >>> 0;
        const next2 = (s2 ^ s0) >>> 0;
        const next3 = (s3 ^ s1) >>> 0;
        const next1 = (s1 ^ next2) >>> 0;
        const next0 = (s0 ^ next3) >>> 0;
        this.#words = [next0, next1, (next2 ^ shifted) >>> 0, rotateLeft(next3, 11)];
        this.#draws += 1n;
        return result;
    }
    uniform() {
        return this.nextUint32() / 0x1_0000_0000;
    }
    bounded(bound) {
        if (!Number.isSafeInteger(bound) || bound < 1 || bound > 0x1_0000_0000) {
            throw new TypeError("bound must be an integer from 1 through 2^32");
        }
        const limit = Math.floor(0x1_0000_0000 / bound) * bound;
        let value = this.nextUint32();
        while (value >= limit)
            value = this.nextUint32();
        return value % bound;
    }
    snapshot() {
        return Object.freeze({
            words: Object.freeze([...this.#words]),
            draws: this.#draws,
        });
    }
}
export function deriveRandomState(rootSeed, streamPath) {
    if (!/^(?:[0-9a-f]{2})+$/.test(rootSeed))
        throw new TypeError("root seed must be lowercase even-length hex");
    if (streamPath.some((part) => part.length === 0))
        throw new TypeError("stream path parts cannot be empty");
    const digest = sha256(utf8ToBytes(JSON.stringify(["e308-rng-v1", rootSeed, streamPath])));
    const view = new DataView(digest.buffer, digest.byteOffset, digest.byteLength);
    const words = [0, 4, 8, 12].map((offset) => view.getUint32(offset, true));
    if (words.every((word) => word === 0))
        words[0] = 1;
    validateWords(words);
    return Object.freeze({ words: Object.freeze([...words]), draws: 0n });
}
export class RandomStreams {
    #rootSeed;
    #streams = new Map();
    constructor(rootSeed, restored = []) {
        deriveRandomState(rootSeed, ["validation"]);
        this.#rootSeed = rootSeed;
        for (const stream of restored) {
            const key = JSON.stringify(stream.path);
            if (this.#streams.has(key))
                throw new TypeError(`Duplicate random stream: ${key}`);
            this.#streams.set(key, new Xoshiro128(stream));
        }
    }
    open(path) {
        const key = JSON.stringify(path);
        let stream = this.#streams.get(key);
        if (!stream) {
            stream = new Xoshiro128(deriveRandomState(this.#rootSeed, path));
            this.#streams.set(key, stream);
        }
        return stream;
    }
    snapshot() {
        const streams = [...this.#streams.entries()]
            .sort(([left], [right]) => (left < right ? -1 : left > right ? 1 : 0))
            .map(([path, random]) => Object.freeze({ path: Object.freeze(JSON.parse(path)), ...random.snapshot() }));
        return Object.freeze({
            algorithm: "xoshiro128ss-v1",
            derivation: "sha256-path-v1",
            rootSeed: this.#rootSeed,
            streams: Object.freeze(streams),
        });
    }
    clone() {
        const snapshot = this.snapshot();
        return new RandomStreams(snapshot.rootSeed, snapshot.streams);
    }
}
//# sourceMappingURL=xoshiro.js.map